import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Typography,
  Paper,
  Toolbar,
  InputAdornment,
  Snackbar,
  Alert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import axios from "axios";
import ActionsMenu from "./ActionsMenu";
import Swal from "sweetalert2";

const initialForm = {
  advance_id: "",
  expense_id: "",
  settled_amount: "",
  settlement_date: "",
  expense_head_id: "",
  description: "",
};

const formatDateForInput = (value) => {
  if (!value) return "";
  return new Date(value).toISOString().slice(0, 10);
};

export default function AdvanceSettlement() {
  const [rows, setRows] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [expenseHeads, setExpenseHeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);

  const user = JSON.parse(sessionStorage.getItem("user"));
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const clientId = user?.client_id;
  const collegeId = user?.college_id;

  const fetchLookups = async () => {
    try {
      const [advanceRes, expenseRes, headRes] = await Promise.all([
        axios.get(
          `${API_BASE_URL}/api/adv-exp-advance/${clientId}/${collegeId}?page=1&pageSize=500`
        ),
        axios.get(
          `${API_BASE_URL}/api/adv-exp-expense/${clientId}/${collegeId}?page=1&pageSize=500`
        ),
        axios.get(
          `${API_BASE_URL}/api/adv-exp-expense-head/${clientId}/${collegeId}?page=1&pageSize=500`
        ),
      ]);
      setAdvances(advanceRes.data?.data?.items || []);
      setExpenses(expenseRes.data?.data?.items || []);
      setExpenseHeads(headRes.data?.data?.items || []);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Failed to load dropdown data",
        severity: "error",
      });
    }
  };

  // Fetch settlements data (server-side pagination)
  const fetchData = async (page = 0, pageSize = 10, search = "") => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/adv-exp-advance-settlement/${clientId}/${collegeId}?page=${
          page + 1
        }&pageSize=${pageSize}&search=${encodeURIComponent(search)}`
      );
      if (data.success) {
        setRows((data.data.items || []).map((r) => ({ id: r.settlement_id, ...r })));
        setRowCount(data.data.total || 0);
      } else {
        setSnackbar({
          open: true,
          message: data.message || "Failed to load data",
          severity: "error",
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Failed to load data",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId && collegeId && clientId !== "null" && collegeId !== "null") {
      fetchLookups();
    }
    // eslint-disable-next-line
  }, [clientId, collegeId]);

  useEffect(() => {
    if (clientId && collegeId && clientId !== "null" && collegeId !== "null") {
      fetchData(page, pageSize, search);
    }
    // eslint-disable-next-line
  }, [clientId, collegeId, page, pageSize, search]);

  const handleOpen = () => {
    setEditing(null);
    setForm(initialForm);
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const handleEdit = (row) => {
    setEditing(row);
    setForm({
      advance_id: row.advance_id || "",
      expense_id: row.expense_id || "",
      settled_amount: row.settled_amount || "",
      settlement_date: formatDateForInput(row.settlement_date),
      expense_head_id: row.expense_head_id || "",
      description: row.description || "",
    });
    setOpen(true);
  };

  const handleDelete = async (row) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This will delete the settlement and recalculate the advance balance.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, keep it!",
    });
    if (!result.isConfirmed) return;
    try {
      await axios.delete(
        `${API_BASE_URL}/api/adv-exp-advance-settlement/${clientId}/${collegeId}/${row.settlement_id}`
      );
      await Swal.fire("Deleted!", "Your settlement has been deleted.", "success");
      fetchLookups();
      fetchData(page, pageSize, search);
    } catch (err) {
      await Swal.fire(
        "Oops!",
        err?.response?.data?.message || "Delete failed. Please try again.",
        "error"
      );
    }
  };

  const handleSubmit = async () => {
    try {
      if (editing) {
        await axios.put(
          `${API_BASE_URL}/api/adv-exp-advance-settlement/${clientId}/${collegeId}/${editing.settlement_id}`,
          {
            ...form,
            updated_by: user?.user_email || "system",
            updated_on: new Date().toISOString(),
          }
        );
        setSnackbar({ open: true, message: "Updated successfully", severity: "success" });
      } else {
        await axios.post(
          `${API_BASE_URL}/api/adv-exp-advance-settlement/${clientId}/${collegeId}`,
          {
            ...form,
            college_id: collegeId,
            created_by: user?.user_email || "system",
          }
        );
        setSnackbar({ open: true, message: "Created successfully", severity: "success" });
      }
      setOpen(false);
      fetchLookups();
      fetchData(page, pageSize, search);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Save failed",
        severity: "error",
      });
    }
  };

  const handleExpenseChange = (expenseId) => {
    const selectedExpense = expenses.find((expense) => expense.expense_id === Number(expenseId));
    setForm({
      ...form,
      expense_id: expenseId,
      expense_head_id: selectedExpense?.expense_head_id || form.expense_head_id,
    });
  };

  const columns = [
    {
      field: "serial",
      headerName: "S. No.",
      width: 90,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return params.api.getRowIndex(params.id) + 1;
      },
    },
    {
      field: "settlement_date",
      headerName: "Date",
      flex: 0.7,
      renderCell: (params) => formatDateForInput(params.row.settlement_date),
    },
    {
      field: "advance",
      headerName: "Advance",
      flex: 1.2,
      renderCell: (params) => params.row.advance?.party?.party_name || "",
    },
    {
      field: "expense",
      headerName: "Expense",
      flex: 1,
      renderCell: (params) =>
        params.row.expense
          ? `${params.row.expense.bill_no || "Expense"} - ${params.row.expense.amount}`
          : "",
    },
    {
      field: "expense_head",
      headerName: "Expense Head",
      flex: 1,
      renderCell: (params) => params.row.expense_head?.expense_head_name || "",
    },
    { field: "settled_amount", headerName: "Settled Amount", flex: 0.8 },
    { field: "description", headerName: "Description", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      sortable: false,
      renderCell: (params) => (
        <ActionsMenu row={params.row} onEdit={handleEdit} onDelete={handleDelete} />
      ),
    },
  ];

  return (
    <Box p={2}>
      <Paper elevation={3} sx={{ p: 2 }}>
        <Toolbar>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            Advance Settlements
          </Typography>
          <TextField
            size="medium"
            placeholder="Search..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mr: 2 }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpen}
            sx={{ color: "#fff" }}
          >
            Add Settlement
          </Button>
        </Toolbar>
        <Box height={400} mt={2}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            page={page}
            pageSize={pageSize}
            rowCount={rowCount}
            pagination
            paginationMode="server"
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
            disableSelectionOnClick
            autoHeight
          />
        </Box>
      </Paper>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? "Edit Settlement" : "Add Settlement"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              select
              margin="normal"
              label="Advance"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.advance_id}
              onChange={(e) => setForm({ ...form, advance_id: e.target.value })}
              SelectProps={{ native: true }}
              required
            >
              <option value="">Select Advance</option>
              {advances.map((advance) => (
                <option key={advance.advance_id} value={advance.advance_id}>
                  {advance.party?.party_name || "Advance"} - Balance: {advance.balance_amount}
                </option>
              ))}
            </TextField>
            <TextField
              select
              margin="normal"
              label="Expense"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.expense_id}
              onChange={(e) => handleExpenseChange(e.target.value)}
              SelectProps={{ native: true }}
            >
              <option value="">None</option>
              {expenses.map((expense) => (
                <option key={expense.expense_id} value={expense.expense_id}>
                  {expense.bill_no || "Expense"} - {expense.party?.party_name || ""} -{" "}
                  {expense.amount}
                </option>
              ))}
            </TextField>
            <TextField
              label="Settled Amount"
              value={form.settled_amount}
              onChange={(e) => setForm({ ...form, settled_amount: e.target.value })}
              type="number"
              fullWidth
              required
            />
            <TextField
              label="Settlement Date"
              value={form.settlement_date}
              onChange={(e) => setForm({ ...form, settlement_date: e.target.value })}
              type="date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />
            <TextField
              select
              margin="normal"
              label="Expense Head"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.expense_head_id}
              onChange={(e) => setForm({ ...form, expense_head_id: e.target.value })}
              SelectProps={{ native: true }}
            >
              <option value="">None</option>
              {expenseHeads.map((expenseHead) => (
                <option key={expenseHead.expense_head_id} value={expenseHead.expense_head_id}>
                  {expenseHead.expense_head_name}
                </option>
              ))}
            </TextField>
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" sx={{ color: "#fff" }}>
            {editing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
